import { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { 
  ArrowRight, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  ArrowLeft, 
  Shield, 
  Clock, 
  Users, 
  Award,
  Phone,
  FileText,
  Handshake,
  Target,
  Building2,
  Briefcase,
  Globe,
  Heart
} from 'lucide-react';
import { getServiceBySlug, services } from '../data/services';
import { SEO } from '../components/SEO';

export const ServicePage = ({ onOpenLeadPopup }) => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const [openFaq, setOpenFaq] = useState(null);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const IconComponent = service.icon;

  // Get related services (same category, excluding current)
  const relatedServices = services
    .filter(s => s.category === service.category && s.id !== service.id)
    .slice(0, 3);

  // Why Choose Us data
  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Proven Expertise',
      description: 'Our attorneys have extensive experience handling complex matters in this practice area.'
    },
    {
      icon: Clock,
      title: 'Timely Response',
      description: 'We understand urgency and provide prompt attention to your legal needs.'
    },
    {
      icon: Users,
      title: 'Dedicated Team',
      description: 'A committed team of professionals working together on your behalf.'
    },
    {
      icon: Award,
      title: 'Track Record',
      description: 'A history of successful outcomes for clients across various industries.'
    }
  ];

  // What to Expect data
  const whatToExpect = [
    {
      icon: Phone,
      title: 'Initial Consultation',
      description: 'We begin with a thorough discussion of your situation, goals, and concerns to understand your unique needs.'
    },
    {
      icon: FileText,
      title: 'Strategic Planning',
      description: 'Our team develops a customized legal strategy tailored to achieve your specific objectives efficiently.'
    },
    {
      icon: Handshake,
      title: 'Clear Communication',
      description: 'You\'ll receive regular updates and have direct access to your attorney throughout the engagement.'
    },
    {
      icon: Target,
      title: 'Results-Focused Execution',
      description: 'We work diligently to deliver outcomes that protect your interests and support your business goals.'
    }
  ];

  // Industries data
  const industries = [
    { icon: Building2, name: 'Technology & Software' },
    { icon: Briefcase, name: 'Financial Services' },
    { icon: Globe, name: 'International Business' },
    { icon: Heart, name: 'Healthcare & Life Sciences' },
    { icon: Building2, name: 'Manufacturing' },
    { icon: Users, name: 'Professional Services' }
  ];

  return (
    <div className="page-wrapper" data-testid={`service-page-${slug}`}>
      <SEO 
        title={service.title}
        description={`${service.shortDescription} Legal Emphasis provides expert ${service.title.toLowerCase()} services for businesses across the United States.`}
        keywords={`${service.title.toLowerCase()}, ${service.category.toLowerCase()}, legal services, business law, San Francisco attorney`}
      />
      
      {/* Hero Section */}
      <section className="bg-[#0A2A5E] py-20 md:py-32" data-testid="service-hero">
        <div className="le-container">
          <Link 
            to="/#services-section"
            className="inline-flex items-center gap-2 text-gray-300 hover:text-white transition-colors mb-6"
            data-testid="service-back-link"
          >
            <ArrowLeft size={18} />
            All Services
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 flex items-center justify-center hidden md:flex">
              <IconComponent className="text-white" size={32} strokeWidth={1.5} />
            </div>
            <div>
              <span className="text-sm text-gray-300 uppercase tracking-wider">{service.category}</span>
              <h1 className="le-heading-1 text-white mt-2">{service.title}</h1>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="le-section bg-white" data-testid="service-overview">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="le-heading-2 mb-6">Overview</h2>
              <p className="text-gray-600 leading-relaxed text-lg">{service.overview}</p>
            </div>
            <div>
              <div className="bg-[#F8F9FA] p-8">
                <h3 className="font-semibold text-[#0A2A5E] mb-4">Quick Inquiry</h3>
                <p className="text-gray-600 text-sm mb-6">
                  Interested in {service.title.toLowerCase()}? Our team is ready to help.
                </p>
                <button 
                  onClick={onOpenLeadPopup}
                  className="le-btn-primary w-full justify-center"
                  data-testid="service-quick-inquiry-btn"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="le-section bg-[#F8F9FA]" data-testid="service-benefits">
        <div className="le-container">
          <h2 className="le-heading-2 mb-8 md:mb-10">Key Benefits</h2>
          <div className="mobile-scroll-grid benefits-grid grid-cols-1 md:grid-cols-2">
            {service.benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white p-5 md:p-6 flex items-start gap-3 md:gap-4"
                data-testid={`benefit-${index}`}
              >
                <div className="w-7 h-7 md:w-8 md:h-8 bg-[#0A2A5E] flex items-center justify-center flex-shrink-0">
                  <Check className="text-white" size={16} />
                </div>
                <p className="text-gray-700 text-sm md:text-base">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - NEW SECTION */}
      <section className="le-section bg-white" data-testid="service-why-choose">
        <div className="le-container">
          <div className="text-center mb-10 md:mb-12">
            <h2 className="le-heading-2 mb-4">Why Choose Legal Emphasis</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              When it comes to {service.title.toLowerCase()}, experience and dedication make all the difference.
            </p>
          </div>
          <div className="mobile-scroll-grid values-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {whyChooseUs.map((item, index) => {
              const ItemIcon = item.icon;
              return (
                <div 
                  key={index} 
                  className="text-center group bg-white md:bg-transparent p-5 md:p-0 border border-gray-100 md:border-0"
                  data-testid={`why-choose-${index}`}
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-[#F8F9FA] flex items-center justify-center mx-auto mb-4 md:mb-5 group-hover:bg-[#0A2A5E] transition-colors">
                    <ItemIcon className="text-[#0A2A5E] group-hover:text-white transition-colors" size={24} strokeWidth={1.5} />
                  </div>
                  <h4 className="text-base md:text-lg font-semibold text-[#0A2A5E] mb-2">{item.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="le-section bg-[#F8F9FA]" data-testid="service-process">
        <div className="le-container">
          <h2 className="le-heading-2 mb-8 md:mb-10">Our Process</h2>
          <div className="mobile-scroll-grid process-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {service.process.map((step, index) => (
              <div 
                key={step.step}
                className="relative bg-white md:bg-transparent p-5 md:p-0"
                data-testid={`process-step-${index}`}
              >
                <div className="text-5xl md:text-6xl font-bold text-[#0A2A5E]/10 absolute top-3 md:-top-4 left-4 md:left-0">
                  {String(step.step).padStart(2, '0')}
                </div>
                <div className="pt-8">
                  <h4 className="text-base md:text-lg font-semibold text-[#0A2A5E] mb-2 md:mb-3">{step.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect - NEW SECTION */}
      <section className="le-section bg-white" data-testid="service-what-to-expect">
        <div className="le-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="le-heading-2 mb-6">What to Expect</h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                When you engage Legal Emphasis for your {service.title.toLowerCase()} needs, you can expect a seamless, professional experience focused on achieving your goals.
              </p>
              <div className="space-y-6">
                {whatToExpect.map((item, index) => {
                  const ItemIcon = item.icon;
                  return (
                    <div 
                      key={index} 
                      className="flex gap-4"
                      data-testid={`expect-${index}`}
                    >
                      <div className="w-12 h-12 bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                        <ItemIcon className="text-[#0A2A5E]" size={22} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0A2A5E] mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div 
              className="w-full h-[400px] bg-cover bg-center hidden lg:block"
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1685602729277-54538940a06c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBhbmQlMjBncmF5JTIwb2ZmaWNlJTIwaW50ZXJpb3IlMjBlbXB0eSUyMG1vZGVybnxlbnwwfHx8fDE3NzM3OTU5MzZ8MA&ixlib=rb-4.1.0&q=85')`
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Industries We Serve - NEW SECTION */}
      <section className="le-section bg-[#0A2A5E]" data-testid="service-industries">
        <div className="le-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Industries We Serve</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              Our {service.title.toLowerCase()} services support businesses across diverse sectors.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {industries.map((industry, index) => {
              const IndustryIcon = industry.icon;
              return (
                <div 
                  key={index}
                  className="bg-white/10 p-6 text-center hover:bg-white/20 transition-colors"
                  data-testid={`industry-${index}`}
                >
                  <IndustryIcon className="text-white mx-auto mb-3" size={28} strokeWidth={1.5} />
                  <p className="text-white text-sm font-medium">{industry.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="le-section bg-white" data-testid="service-faqs">
        <div className="le-container">
          <div className="max-w-3xl mx-auto">
            <h2 className="le-heading-2 mb-10 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {service.faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-[#E2E8F0]"
                  data-testid={`service-faq-${index}`}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F8F9FA] transition-colors"
                    data-testid={`service-faq-toggle-${index}`}
                  >
                    <span className="font-semibold text-[#0A2A5E] pr-8">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp size={20} className="text-[#0A2A5E] flex-shrink-0" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400 flex-shrink-0" />
                    )}
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      openFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="px-6 pb-6 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      {relatedServices.length > 0 && (
        <section className="le-section bg-[#F8F9FA]" data-testid="related-services">
          <div className="le-container">
            <h2 className="le-heading-2 mb-10">Related Services</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedServices.map((relatedService) => {
                const RelatedIcon = relatedService.icon;
                return (
                  <Link 
                    key={relatedService.id}
                    to={`/services/${relatedService.slug}`}
                    className="bg-white p-8 border border-[#E2E8F0] group hover:border-[#0A2A5E] transition-colors"
                    data-testid={`related-service-${relatedService.slug}`}
                  >
                    <div className="service-icon">
                      <RelatedIcon size={28} strokeWidth={1.5} />
                    </div>
                    <h4 className="text-lg font-semibold text-[#0A2A5E] mb-2 group-hover:text-[#1a4a8e] transition-colors">
                      {relatedService.title}
                    </h4>
                    <p className="text-gray-600 text-sm">{relatedService.shortDescription}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section 
        className="relative py-24 md:py-32"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1681505526188-b05e68c77582?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBzaGFraW5nJTIwaGFuZHMlMjBkZWFsJTIwYWdyZWVtZW50JTIwd2lkZSUyMGFuZ2xlfGVufDB8fHx8MTc3Mzk0NTE2MHww&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
        data-testid="final-cta-section"
      >
        <div className="absolute inset-0 bg-[#0A2A5E]/80"></div>
        <div className="le-container relative z-10 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Schedule Your Consultation Today
          </h2>
          <p className="text-lg text-gray-200 mb-8 max-w-2xl mx-auto">
            Let our experienced attorneys guide you through your legal challenges with confidence.
          </p>
          <button 
            onClick={onOpenLeadPopup}
            className="bg-white text-[#0A2A5E] px-10 py-4 font-semibold uppercase tracking-wider 
                       hover:bg-gray-100 transition-all duration-300 inline-flex items-center justify-center gap-2 btn-lift"
            data-testid="final-cta-btn"
          >
            Get Legal Help
            <ArrowRight size={18} />
          </button>
        </div>
      </section>

    </div>
  );
};
